import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { shallow } from "zustand/shallow";
import { Select, Loader } from "@mantine/core";
import axios from "axios";

import useAppStore from "./report-recipe-runner-store";

import { CustomTooltip } from "@components/shared";

export default function QbProgSelect({ field }) {
  const requestConfig = field.prog_search_config;
  const [items, setItems] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  const cancelRef = useRef(null);
  const requestIdRef = useRef(0);
  const CancelToken = axios.CancelToken;

  const {
    initialQueryData,
    locations,
    orgs,
    queryData,
    queryComponents,
    recipeInfo,
    setQueryData,
  } = useAppStore(
    (state) => ({
      initialQueryData: state.initial_query_data,
      locations: state.locations,
      orgs: state.orgs,
      queryData: state.query_data,
      queryComponents: state.query_components,
      recipeInfo: state.recipe_info,
      setQueryData: state.setQueryData,
    }),
    shallow
  );

  const selectedValue =
    queryData[field.clientName]?.length > 0
      ? `${queryData[field.clientName][0]}`
      : null;

  const keysToResetOnChange = useMemo(
    () =>
      queryComponents
        .filter((f) => f.request_config || f.prog_search_config)
        .filter(
          (f) =>
            f.request_config?.dynamic_payload?.includes(field.server_name) ||
            f.request_config?.additional_payload?.includes(field.server_name) ||
            f.prog_search_config?.dynamic_payload?.includes(
              field.server_name
            ) ||
            f.prog_search_config?.additional_payload?.includes(
              field.server_name
            )
        )
        .map((m) => m.server_name)
        .reduce((acc, cur) => {
          acc[cur] = initialQueryData[cur];
          if (queryData[`${cur}_preserved_options`]) {
            acc[`${cur}_preserved_options`] = [];
          }
          return acc;
        }, {}),
    [queryComponents, field.server_name, initialQueryData, queryData]
  );

  const inputDisabled = useMemo(() => {
    if (!requestConfig) return true;
    if (
      field.type === "effortPicker" ||
      JSON.stringify(requestConfig.dynamic_payload) ===
        `["location_ids","organization_ids"]`
    ) {
      return false;
    }

    if (!requestConfig.dynamic_payload?.length) return false;

    const conditions = requestConfig.dynamic_payload.map((k) => {
      if (k === "location_ids") {
        return locations.length > 0;
      }
      if (k === "organization_ids") {
        return orgs.length > 0;
      }
      if (Array.isArray(queryData[k])) {
        return queryData[k].length > 0;
      }
      return Boolean(queryData[k]);
    });

    return conditions.includes(false);
  }, [requestConfig, field.type, locations, orgs, queryData]);

  const dynamicPayloadValues = requestConfig?.dynamic_payload?.map(
    (m) => queryData[m]
  );
  const additionalPayloadValues = requestConfig?.additional_payload?.map(
    (m) => queryData[m]
  );

  const fetchData = useCallback(() => {
    if (!requestConfig || inputDisabled || !searchValue) return;

    const cancel = cancelRef.current;
    if (cancel) cancel();

    const req = {
      ...requestConfig.data,
      search_value: searchValue,
      report_recipe_id: recipeInfo.id,
    };

    if (locations) req.location_ids = locations.map((m) => m.id);
    if (orgs) req.organization_ids = orgs.map((m) => m.id);

    if (requestConfig.dynamic_payload) {
      requestConfig.dynamic_payload.forEach((k) => {
        if (k === "location_ids") {
          req.location_ids = locations.map((m) => m.id);
        } else if (k === "organization_ids") {
          req.organization_ids = orgs.map((m) => m.id);
        } else {
          req[k] = queryData[k];
        }
      });
    }

    if (requestConfig.additional_payload) {
      requestConfig.additional_payload.forEach((k) => {
        if (k === "location_ids") {
          req.location_ids = locations.map((m) => m.id);
        } else if (k === "organization_ids") {
          req.organization_ids = orgs.map((m) => m.id);
        } else {
          req[k] = queryData[k];
        }
      });
    }

    const requestId = ++requestIdRef.current;

    setLoading(true);

    axios
      .post(requestConfig.api_endpoint, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) return;
        setItems(data.response);
        setLoading(false);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        if (!axios.isCancel(err)) {
          setItems([]);
        }
        setLoading(false);
      });
  }, [
    requestConfig,
    inputDisabled,
    searchValue,
    recipeInfo.id,
    locations,
    orgs,
    queryData,
    CancelToken,
  ]);

  useEffect(() => {
    if (!requestConfig) return;
    if (field.options) {
      setItems(field.options);
      return;
    }
    if (!searchValue) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchData();
  }, [requestConfig, field.options, searchValue, fetchData]);

  useEffect(() => {
    if (!requestConfig || field.options || !searchValue || inputDisabled) {
      return;
    }
    fetchData();
  }, [
    requestConfig,
    field.options,
    searchValue,
    inputDisabled,
    fetchData,
    JSON.stringify(dynamicPayloadValues),
    JSON.stringify(additionalPayloadValues),
    JSON.stringify(locations),
    JSON.stringify(orgs),
  ]);

  useEffect(() => {
    if (!selectedValue) {
      setSearchValue("");
    }
  }, [selectedValue]);

  const formattedOptions = items.map((m) => ({
    label: m.display_text,
    value: `${m.value}`,
  }));

  const selectData = useMemo(() => {
    const preserved = queryData[`${field.clientName}_preserved_options`] || [];
    const merged = [...preserved];
    formattedOptions.forEach((opt) => {
      if (!merged.some((m) => m.value === opt.value)) {
        merged.push(opt);
      }
    });
    return merged;
  }, [formattedOptions, queryData[`${field.clientName}_preserved_options`]]);

  function handleSearchChange(value) {
    setSearchValue(value);
    if (value) {
      setLoading(true);
    } else {
      setLoading(false);
      setItems([]);
    }
  }

  function handleChange(value) {
    if (!value) {
      setSearchValue("");
      setLoading(false);
      setQueryData({
        ...queryData,
        ...keysToResetOnChange,
        [field.clientName]: [],
        [`${field.clientName}_preserved_options`]: [],
      });
      return;
    }

    const foundOpt =
      items.find((f) => `${f.value}` === value) ||
      queryData[`${field.clientName}_preserved_options`]?.find(
        (f) => `${f.value}` === value
      );

    if (!foundOpt) return;

    const displayText = foundOpt.display_text || foundOpt.label;
    const optValue = foundOpt.value;

    const queryDataToSet = foundOpt.query_data_to_set || {};
    queryComponents
      .filter((f) => f.on_select_populate)
      .forEach((qc) => {
        if (queryDataToSet[qc.on_select_populate]) {
          queryDataToSet[qc.clientName] = queryDataToSet[qc.on_select_populate];
          delete queryDataToSet[qc.on_select_populate];
        }
      });

    setSearchValue("");
    setItems([]);
    setLoading(false);

    setQueryData({
      ...queryData,
      ...keysToResetOnChange,
      ...queryDataToSet,
      [`${field.clientName}_preserved_options`]: [
        {
          value: `${optValue}`,
          label: displayText,
        },
      ],
      [field.clientName]: [optValue],
    });
  }

  if (!requestConfig) return null;

  return (
    <Select
      data={selectData}
      labelProps={{
        labelElement: "div",
        style: {
          display: "flex",
          alignItems: "center",
          gap: "6px",
        },
      }}
      label={
        field.tooltip_text ? (
          <CustomTooltip label={field.tooltip_text}>
            <label>{field.label}</label>
          </CustomTooltip>
        ) : (
          <label>{field.label}</label>
        )
      }
      disabled={inputDisabled}
      required={field.required}
      clearable
      searchable
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      value={selectedValue}
      onChange={handleChange}
      onClear={() => handleChange(null)}
      placeholder="Start typing to search..."
      filter={({ options }) => options}
      comboboxProps={{ keepMounted: true }}
      dropdownOpened={Boolean(searchValue) && !selectedValue}
      nothingFoundMessage={
        loading
          ? "Searching..."
          : searchValue
          ? "No results found"
          : "Start typing to search"
      }
      rightSection={loading ? <Loader size="xs" /> : undefined}
      rightSectionPointerEvents="none"
      limit={25}
    />
  );
}
